import React, { useState } from 'react';
import { Modal } from '../common/Modal.tsx';
import { AlertTriangle, Send, Loader2, MessageSquare } from 'lucide-react';

interface RequestChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void> | void;
  taskTitle: string;
  taskCode?: string;
  isSubmitting?: boolean;
}

export const RequestChangesModal: React.FC<RequestChangesModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  taskTitle,
  taskCode,
  isSubmitting = false,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Please provide feedback explaining what needs to be changed.');
      return;
    }
    setError(null);
    try {
      await onSubmit(reason.trim());
      setReason('');
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to request changes.');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isSubmitting) {
          setReason('');
          setError(null);
          onClose();
        }
      }}
      title="Send for Rework"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-amber-900 dark:text-amber-300 text-xs leading-relaxed">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <span className="font-semibold text-amber-900 dark:text-amber-200 break-words">
              Sending {taskCode ? `${taskCode} - ` : ''}&ldquo;{taskTitle}&rdquo; back to In Progress.
            </span>
            <p className="mt-1 text-surface-700 dark:text-slate-300">
              Explain clearly what needs rework. The card will be flagged with a <strong className="text-rose-600 dark:text-rose-400">Rework</strong> badge, positioned at the top of In Progress, and the assignee will be notified immediately.
            </p>
          </div>
        </div>

        {error && (
          <div className="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 text-rose-700 dark:text-rose-300 text-xs font-medium">
            {error}
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-surface-700 dark:text-slate-300 mb-1.5 flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5 text-brand-500" />
            Rework Notes / Reason <span className="text-rose-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError(null);
            }}
            placeholder="e.g. Please verify edge case handling for invalid token refresh, and add unit test coverage for the new route handler..."
            rows={4}
            disabled={isSubmitting}
            className="w-full px-3 py-2 text-sm bg-surface-50 dark:bg-slate-900/60 border border-surface-200 dark:border-slate-700/80 rounded-xl text-surface-900 dark:text-slate-100 placeholder-surface-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 resize-none"
            autoFocus
          />
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-surface-200 dark:border-slate-800">
          <button
            type="button"
            onClick={() => {
              setReason('');
              setError(null);
              onClose();
            }}
            disabled={isSubmitting}
            className="px-3.5 py-1.5 text-xs font-medium text-surface-600 dark:text-slate-300 hover:text-surface-900 dark:hover:text-slate-100 hover:bg-surface-100 dark:hover:bg-slate-800 rounded-xl transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !reason.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-500 active:bg-rose-700 rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Sending Back...
              </>
            ) : (
              <>
                <Send className="w-3.5 h-3.5" />
                Send for Rework
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};
